import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Mail, Phone, MapPin, Globe, Calendar, CheckCircle2, Clock, XCircle,
  KeyRound, Copy, Send, Tag, Activity, Inbox,
} from "lucide-react";

/**
 * Slide-in drawer with rich detail on a single Ronin lead.
 * Click any row in the leads table → drawer opens → admin can:
 *   - See full profile + geo + verification + newsletter drip + booking history
 *   - Send a password reset email
 *   - Re-send the verification email
 *   - Copy email / phone
 */
export function RoninLeadDrawer({ leadId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState("");

  useEffect(() => {
    if (!leadId || !open) { setData(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/ronin-leads/by-id/${leadId}`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load lead");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leadId, open]);

  const sendPasswordReset = async () => {
    if (!data) return;
    setActionBusy("reset");
    try {
      await api.post(`/admin/ronin-leads/by-id/${data.id}/send-password-reset`);
      toast.success(`Password reset link sent to ${data.email}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send reset link");
    } finally { setActionBusy(""); }
  };

  const resendVerification = async () => {
    if (!data) return;
    setActionBusy("verify");
    try {
      const res = await api.post(`/admin/ronin-leads/by-id/${data.id}/resend-verification`);
      if (res.data?.already_verified) {
        toast.info("This account is already verified.");
      } else {
        toast.success(`Verification email re-sent to ${data.email}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't resend");
    } finally { setActionBusy(""); }
  };

  const copy = async (label, val) => {
    if (!val) return;
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch { toast.error("Couldn't copy"); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="bg-[#0A0A0A] border-l border-[#27272A] text-white w-full sm:max-w-[560px] overflow-y-auto p-0"
        data-testid="ronin-lead-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#27272A]">
          <SheetTitle className="text-white font-display uppercase tracking-tight text-2xl">
            {loading ? "Loading…" : data ? `${data.first_name || ""} ${data.last_name || ""}`.trim() || data.email : "Lead detail"}
          </SheetTitle>
          <SheetDescription className="text-zinc-400 text-xs">
            {data?.email ? <span className="font-mono">{data.email}</span> : null}
          </SheetDescription>
        </SheetHeader>

        {loading || !data ? (
          <div className="p-10 flex items-center justify-center text-zinc-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> {loading ? "Loading…" : "No data"}
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Status badges */}
            <div className="flex flex-wrap gap-2">
              {data.email_verified ? (
                <Badge color="emerald" icon={CheckCircle2} label="Verified" testid="lead-badge-verified" />
              ) : (
                <Badge color="amber" icon={Clock} label="Pending verification" testid="lead-badge-pending" />
              )}
              {data.marketing_consent ? (
                <Badge color="zinc" icon={Inbox} label="On drip" testid="lead-badge-drip" />
              ) : null}
              {data.newsletter?.unsubscribed ? (
                <Badge color="red" icon={XCircle} label="Unsubscribed" testid="lead-badge-unsub" />
              ) : null}
              {data.signup_source ? (
                <Badge color="zinc" icon={Tag} label={data.signup_source.replace(/_/g, " ")} testid="lead-badge-source" />
              ) : null}
            </div>

            {/* Action row */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={sendPasswordReset} disabled={!!actionBusy}
                className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest font-bold text-[10px]"
                data-testid="lead-action-reset"
              >
                {actionBusy === "reset" ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <KeyRound className="h-3 w-3 mr-2" />}
                Send password reset
              </Button>
              <Button
                onClick={resendVerification} disabled={!!actionBusy || data.email_verified}
                variant="outline"
                className="border-[#27272A] bg-[#050505] text-zinc-200 hover:text-white uppercase tracking-widest font-bold text-[10px] disabled:opacity-40"
                data-testid="lead-action-resend"
              >
                {actionBusy === "verify" ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Send className="h-3 w-3 mr-2" />}
                {data.email_verified ? "Already verified" : "Resend verification"}
              </Button>
            </div>

            {/* Contact */}
            <Section title="Contact" icon={Mail}>
              <Row label="Email" value={data.email} copyable onCopy={() => copy("Email", data.email)} testid="lead-email" />
              <Row label="Phone" value={data.phone || "—"} copyable={!!data.phone} onCopy={() => copy("Phone", data.phone)} icon={Phone} testid="lead-phone" />
            </Section>

            {/* Location */}
            <Section title="Location" icon={MapPin}>
              <Row label="Country" value={data.country || "—"} testid="lead-country" />
              <Row label="Postal / ZIP" value={data.zip_code || "—"} testid="lead-zip" />
              <Row label="City / Region" value={
                [data.geo_place, data.geo_region].filter(Boolean).join(", ") || "—"
              } testid="lead-place" />
            </Section>

            {/* Training profile — captured during the mobile onboarding flow */}
            {data.training_prefs && Object.keys(data.training_prefs).length > 0 ? (
              <Section title="Training profile" icon={Activity}>
                <Row label="Level" value={fmtPref(data.training_prefs.level)} testid="lead-train-level" />
                <Row label="Goal" value={fmtPref(data.training_prefs.goal)} testid="lead-train-goal" />
                <Row label="Frequency" value={fmtFreq(data.training_prefs.frequency)} testid="lead-train-freq" />
              </Section>
            ) : null}

            {/* Verification + auth */}
            <Section title="Account" icon={KeyRound}>
              <Row label="Signed up" value={fmtDate(data.created_at)} testid="lead-created" />
              <Row label="Verification sent" value={fmtDate(data.verification_sent_at)} testid="lead-verif-sent" />
              <Row label="Verified at" value={fmtDate(data.email_verified_at) || "—"} testid="lead-verified-at" />
              <Row label="Last password reset" value={fmtDate(data.password_reset_completed_at) || (data.password_reset_requested_at ? `Requested ${fmtDate(data.password_reset_requested_at)}` : "Never")} testid="lead-reset" />
              <Row label="Referral code" value={data.referral_code || "—"} copyable={!!data.referral_code} onCopy={() => copy("Referral code", data.referral_code)} testid="lead-referral" />
            </Section>

            {/* Consent */}
            <Section title="Consent" icon={CheckCircle2}>
              <Row label="Marketing opt-in" value={data.marketing_consent ? `Yes · ${fmtDate(data.marketing_consent_at) || "—"}` : "No"} testid="lead-marketing" />
              <Row label="Terms accepted" value={fmtDate(data.terms_accepted_at) || "—"} testid="lead-terms" />
            </Section>

            {/* Newsletter */}
            {data.newsletter ? (
              <Section title="Newsletter drip" icon={Inbox}>
                <Row label="Subscribed" value={fmtDate(data.newsletter.subscribed_at)} testid="lead-sub-at" />
                <Row label="Source" value={data.newsletter.source || "—"} testid="lead-sub-source" />
                <Row label="Drip step" value={
                  data.newsletter.unsubscribed
                    ? "Unsubscribed"
                    : `Step ${data.newsletter.current_step ?? -1} of 6 · next ${fmtDate(data.newsletter.next_send_at) || "—"}`
                } testid="lead-sub-step" />
              </Section>
            ) : null}

            {/* Bookings */}
            <Section title="Bookings" icon={Calendar}>
              <Row label="Total bookings" value={data.bookings?.count ?? 0} testid="lead-bookings-count" />
              {data.bookings?.last ? (
                <Row label="Last booking" value={
                  `${data.bookings.last.status || "?"} · ${data.bookings.last.starts_at ? fmtDate(data.bookings.last.starts_at) : "unscheduled"}`
                } testid="lead-bookings-last" />
              ) : (
                <Row label="Last booking" value="—" testid="lead-bookings-last" />
              )}
            </Section>

            <p className="text-[10px] text-zinc-600 leading-relaxed">
              <Activity className="h-3 w-3 inline mr-1" /> User ID:{" "}
              <span className="font-mono">{data.id}</span>
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── Bits ──────────────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }) {
  return (
    <div className="border border-[#27272A] bg-[#050505] rounded-sm">
      <div className="px-4 py-2 border-b border-[#27272A] flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-[#FF331F]" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">{title}</span>
      </div>
      <div className="divide-y divide-[#1a1a1a]">{children}</div>
    </div>
  );
}

function Row({ label, value, copyable, onCopy, testid }) {
  return (
    <div className="px-4 py-2 grid grid-cols-[110px_1fr_auto] gap-3 items-center" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{label}</div>
      <div className="text-sm text-white truncate">{value ?? "—"}</div>
      {copyable ? (
        <button onClick={onCopy} className="text-zinc-500 hover:text-white" title={`Copy ${label}`}>
          <Copy className="h-3.5 w-3.5" />
        </button>
      ) : <span />}
    </div>
  );
}

function Badge({ color, icon: Icon, label, testid }) {
  const palette = {
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    amber:   "bg-amber-500/10  text-amber-400  border-amber-500/30",
    red:     "bg-red-500/10    text-red-400    border-red-500/30",
    zinc:    "bg-zinc-500/10   text-zinc-300   border-zinc-500/30",
  }[color] || "bg-zinc-500/10 text-zinc-300 border-zinc-500/30";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-sm text-[10px] uppercase tracking-widest font-bold ${palette}`} data-testid={testid}>
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// training_prefs values are stored as snake_case ids — render them human-readable.
function fmtPref(val) {
  if (!val) return "—";
  return String(val).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtFreq(val) {
  const map = { "2-3": "2–3× a week", "4-5": "4–5× a week", daily: "Every day" };
  return val ? (map[val] || fmtPref(val)) : "—";
}
